import { useCallback, useEffect, useState } from "react";
import type { ClientSafeWebsiteOwnershipViewV1 } from "@enrein/shared-contracts/contracts/website-ownership-record";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

export function WebsiteOwnershipChecklistPanel({
  api,
  clientLinkId,
  token,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
}) {
  const [view, setView] = useState<ClientSafeWebsiteOwnershipViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setView(await api.getClientWebsiteOwnershipRecord(clientLinkId, token));
    } catch (err) {
      setView(null);
      const message = err instanceof Error ? err.message : String(err);
      if (
        !message.includes("client_website_ownership_not_ready") &&
        !message.includes("client_portal_api_error_404")
      ) {
        setError(toClientApiErrorMessage(message));
      }
    } finally {
      setBusy(false);
    }
  }, [api, clientLinkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (busy && !view) return <p className="muted">Loading ownership checklist…</p>;
  if (error) return <p className="error">{error}</p>;
  if (!view) return null;

  return (
    <article className="value-card">
      <p className="value-card-eyebrow">Website ownership checklist</p>
      <h3 className="value-card-headline">{view.headline}</h3>
      <p>{view.subcopy}</p>
      <p className="muted">
        Heuristic gap indicator: {view.riskScoreLabel} — {view.riskScoreExplanation}
      </p>
      <ul>
        {view.checklist.map((item) => (
          <li key={item.label}>
            <strong>{item.stateLabel}</strong> — {item.label}
          </li>
        ))}
      </ul>
      <details>
        <summary>Disclaimers</summary>
        <ul>
          {view.disclaimers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </details>
    </article>
  );
}
